import type { BillingPurchaseRequest } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { getApiClientErrorStatus } from '@/services/api/errors';
import { accountStore } from '@/services/auth/store';
import { pollDelay, pollTask } from '@/utils/poll-task';
import { sentryCaptureException } from '@/utils/sentry';

// The workflow raises and collects the invoice after the POST returns, so the poll covers the whole
// purchase, the same as the Ordway rails.
const WALLET_PURCHASE_POLL_MAX_ATTEMPTS = 90;
const WALLET_PURCHASE_POLL_INTERVAL_MS = 2000;

/** Thrown by {@link startWalletPurchase} on a 409: another purchase on this account is still running. */
export class WalletPurchaseInProgressError extends Error {
  constructor() {
    super(WALLET_PURCHASE_IN_PROGRESS_MESSAGE);
  }
}

export const WALLET_PURCHASE_IN_PROGRESS_MESSAGE = 'A purchase is already in progress. Try again after it completes.';
const WALLET_PURCHASE_DECLINED_MESSAGE = 'Your card was declined.';
const WALLET_PURCHASE_FAILED_MESSAGE = "Your purchase couldn't be processed. Contact support.";
const WALLET_PURCHASE_TIMED_OUT_MESSAGE = "Your payment is processing. Contact support if your balance doesn't update.";
// The run waits 15 minutes for the browser to confirm (`purchaseConfirmBudget` in services), and a new
// purchase gets a 409 until it gives up.
export const WALLET_PURCHASE_NOT_CHARGED_MESSAGE = "Your card wasn't charged. Try again in 15 minutes.";
// Before the confirmation the 15-minute wait has not started, and the steps ahead of it can take several
// more minutes.
export const WALLET_PURCHASE_NOT_CHARGED_EARLY_MESSAGE = "Your card wasn't charged. Try again in about 20 minutes.";

export type WalletPurchaseRequest = BillingPurchaseRequest;

/**
 * Starts a modern Stripe purchase: a card to put on file, funds to deposit into the wallet, or both. Resolves
 * with the run id as soon as the workflow starts, so the caller must poll it with
 * {@link pollForWalletPurchaseTask}. A 409 (another purchase is in flight) throws
 * {@link WalletPurchaseInProgressError}.
 */
export async function startWalletPurchase(body: WalletPurchaseRequest): Promise<string> {
  const account = accountStore.get();
  if (account == null) {
    throw new Error('Cannot add funds without an account');
  }

  try {
    const response = await hydraUiApi.billing.purchase({
      params: { path: { account_id: String(account.id) } },
      body,
    });
    return response.run_id;
  } catch (error) {
    if (getApiClientErrorStatus(error) === 409) {
      throw new WalletPurchaseInProgressError();
    }
    throw error;
  }
}

/**
 * Polls `billing/purchase_status/{run_id}` until the purchase ends. On `requires_confirmation` it calls
 * `confirmPayment` once with the client secret (for example a 3D Secure challenge), then keeps polling.
 *
 * Resolves `true` on `success`, and `false` when `cancelAll` stopped it. Throws the user-facing message on
 * a decline, a failure, a timeout or a lost account. A timeout does not mean the card was not charged, so
 * its message does not ask the user to try again.
 */
export const pollForWalletPurchaseTask = pollTask(
  async (signal, runId: string, confirmPayment: (clientSecret: string) => Promise<void>): Promise<boolean> => {
    const account = accountStore.get();
    if (account == null) {
      // The workflow is already running. Resolving `false` would read as a cancelled poll and show nothing.
      throw new Error(WALLET_PURCHASE_NOT_CHARGED_EARLY_MESSAGE);
    }
    const params = { path: { account_id: String(account.id), run_id: runId } };
    const confirmedSecrets = new Set<string>();

    for (let attempt = 0; attempt < WALLET_PURCHASE_POLL_MAX_ATTEMPTS; attempt++) {
      if (signal.aborted) {
        return false;
      }

      const response = await readStatus(params);
      if (response == null) {
        await pollDelay(WALLET_PURCHASE_POLL_INTERVAL_MS, signal);
        continue;
      }
      switch (response.status) {
        case 'success':
          return true;
        case 'declined':
          throw new Error(response.message ?? WALLET_PURCHASE_DECLINED_MESSAGE);
        case 'failed':
          throw new Error(WALLET_PURCHASE_FAILED_MESSAGE);
        case 'requires_confirmation': {
          const secret = response.client_secret;
          if (secret != null && !confirmedSecrets.has(secret)) {
            confirmedSecrets.add(secret);
            await confirmPayment(secret);
          }
          break;
        }
        case 'pending':
          break;
        default: {
          const exhaustive: never = response.status;
          throw new Error(String(exhaustive));
        }
      }

      await pollDelay(WALLET_PURCHASE_POLL_INTERVAL_MS, signal);
    }

    sentryCaptureException(
      new Error(`Wallet purchase run ${runId} did not finish after ${WALLET_PURCHASE_POLL_MAX_ATTEMPTS} status reads`),
    );
    // The browser makes the charge on this rail, so a run that never reached a confirmation charged nothing.
    throw new Error(
      confirmedSecrets.size > 0 ? WALLET_PURCHASE_TIMED_OUT_MESSAGE : WALLET_PURCHASE_NOT_CHARGED_EARLY_MESSAGE,
    );
  },
);

// A 4xx other than 429 (an unknown or malformed run) will not change, so it ends the poll. Any other
// failed read says nothing about the run, so it returns `undefined` and the loop polls again.
async function readStatus(params: { path: { account_id: string; run_id: string } }) {
  try {
    return await hydraUiApi.billing.getPurchaseStatus({ params });
  } catch (error) {
    const status = getApiClientErrorStatus(error);
    if (status != null && status >= 400 && status < 500 && status !== 429) {
      sentryCaptureException(error);
      throw new Error(WALLET_PURCHASE_FAILED_MESSAGE);
    }
    return undefined;
  }
}
